import React, { FunctionComponent, useEffect, useState } from "react";
import {
  ReportedProductDelivery,
  ReportedStocktaking
} from "../../../models/StoreInventory";
import MaterialTable, { MTableToolbar } from "@material-table/core";
import moment from "moment";
import {
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  TextField
} from "@material-ui/core";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import { StoreInventoryItemEditor } from "../StoreList/components/StoreInventoryItemEditor";
import useStyles from "./styles";
import { OrderUnit } from "../../../models/Order";
import * as ProductApi from "../../../api/productApi";
import { Alert } from "@material-ui/lab";
import { ConfirmDialog } from "../../../components/dialogs/ConfirmDialog";
import { useI18n } from "../../../hooks/useI18n";

interface ProductDeliveriesProps {
  inventoryItem: any;
  storeId: any;
  onSave: (deliveries: any, quantity: number) => void;
}

export const ProductDeliveries: FunctionComponent<ProductDeliveriesProps> = ({
  inventoryItem,
  storeId,
  onSave
}: any) => {
  const classes = useStyles();
  const { t } = useI18n();

  const [state, setState] = useState({
    deliveries: [] as ReportedProductDelivery[],
    showUpdateDelivery: false,
    showDeleteDelivery: false,
    deliveryId: "",
    quantity: 0,
    previousQuantity: 0,
    deliverySuccess: false,
    deliveryFailed: false,
    deliveryDeleteSuccess: false,
    deliveryDeleteFailed: false
  });

  useEffect(() => {
    if (inventoryItem && inventoryItem.reportedProductDelivery) {
      const deliveries = inventoryItem.reportedProductDelivery.reduce(
        (acc: any, curr: any) => {
          if (
            inventoryItem.reportedStocktaking.findIndex(
              (x: any) =>
                x.id === curr.storeStocktakingId ||
                (x.onShelf === curr.onShelfQuantity &&
                  curr.onShelfQuantityBeforeDelivery === 0 &&
                  curr.reportedBy === x.reportedBy &&
                  moment(curr.created).diff(x.updated, "minutes") <= 2)
            ) === -1
          ) {
            acc.push(curr);
          } else {
            acc.push({ ...curr, hidden: true });
          }
          return acc;
        },
        []
      );

      deliveries.sort((a: any, b: any) => a.created - b.created);

      const latestDelivery = deliveries.find((x: any) => !x.hidden);

      setState({
        ...state,
        deliveries: deliveries,
        deliveryId: latestDelivery ? latestDelivery.id : null
      });
    }
  }, [inventoryItem]);

  const calculateQuantity = (quantity: number) => {
    const index = state.deliveries.findIndex(
      (x: any) => x.id === state.deliveryId
    );
    if (index === 0) {
      return (
        inventoryItem.onShelf -
        state.previousQuantity +
        (quantity ? quantity : 0)
      );
    }

    return inventoryItem.onShelf;
  };

  const calculateOnShelfAfterRemove = () => {
    const index = state.deliveries.findIndex(
      (x: any) => x.id === state.deliveryId
    );
    if (index === 0) {
      return inventoryItem.onShelf - state.deliveries[index].onShelfQuantity;
    }

    return inventoryItem.onShelf;
  };

  const populateDeliveries = (): ReportedProductDelivery[] => {
    return state.deliveries
      ? state.deliveries.filter((x: any) => !x.hidden)
      : [];
  };

  return inventoryItem ? (
    <>
      <MaterialTable
        title=""
        columns={[
          {
            title: t("common.tableHeaders.created"),
            field: "created",
            cellStyle: { padding: "8px" },
            render: delivery => {
              return moment(delivery.created).format("YYYY-MM-DD HH:mm");
            }
          },
          {
            title: t("common.tableHeaders.reportedBy"),
            field: "reportedBy",
            cellStyle: { padding: "8px" }
          },
          {
            title: t("common.tableHeaders.delivery"),
            field: "onShelfQuantity",
            cellStyle: { padding: "8px" },
            render: delivery => {
              return `${delivery.onShelfQuantity} / ${delivery.onShelfQuantityBeforeDelivery}`;
            }
          }
        ]}
        data={populateDeliveries()}
        actions={[
          (rowData: any) => ({
            icon: "edit",
            tooltip: t("common.actions.updateDelivery"),
            disabled: rowData.id !== state.deliveryId,
            iconProps:
              rowData.id !== state.deliveryId
                ? { style: { color: "rgba(0, 0, 0, 0.24)" } }
                : { style: { color: "rgba(0, 0, 0, 0.54)" } },
            onClick: (event, rowData: any) => {
              setState({
                ...state,
                showUpdateDelivery: true,
                quantity: rowData.onShelfQuantity,
                previousQuantity: rowData.onShelfQuantity
              });
            }
          }),
          (rowData: any) => ({
            icon: "delete",
            tooltip: t("common.actions.deleteDelivery"),
            disabled: rowData.id !== state.deliveryId,
            iconProps:
              rowData.id !== state.deliveryId
                ? { style: { color: "rgba(0, 0, 0, 0.24)" } }
                : { style: { color: "rgba(0, 0, 0, 0.54)" } },
            onClick: (event, rowData: any) => {
              setState({
                ...state,
                showDeleteDelivery: true,
                quantity: rowData.onShelfQuantity
              });
            }
          })
        ]}
        components={{
          Container: props => <Paper {...props} elevation={0} />,
          Toolbar: props => (
            <div style={{ height: "0px", padding: 0 }}>
              <MTableToolbar {...props} />
            </div>
          )
        }}
        options={{
          headerStyle: {
            padding: "8px"
          },
          actionsColumnIndex: -1,
          actionsCellStyle: { whiteSpace: "nowrap" },
          pageSize: 10,
          search: false,
          pageSizeOptions: [20, 30, 40],
          sorting: false
        }}
        localization={{
          header: {
            actions: ""
          }
        }}
      />

      <ConfirmDialog
        visible={state.showDeleteDelivery}
        titleText={t("common.deliveryManagement.removeDelivery")}
        bodyText={t("common.deliveryManagement.removeDeliveryConfirmation")}
        confirmText={t("common.actions.remove")}
        onConfirm={async () => {
          const result = await ProductApi.deleteDelivery(state.deliveryId);
          if (result.type === "OK") {
            const deliveries = state.deliveries.filter(
              (d: any) => d.id !== state.deliveryId
            );
            onSave(deliveries, calculateOnShelfAfterRemove());
            setState({
              ...state,
              showDeleteDelivery: false,
              deliveryDeleteSuccess: true,
              deliveryDeleteFailed: false,
              deliveries: deliveries
            });
            return true;
          } else {
            setState({
              ...state,
              deliveryDeleteFailed: true,
              deliveryDeleteSuccess: false
            });
            return false;
          }
        }}
        onCancel={() => {
          setState({ ...state, showDeleteDelivery: false });
        }}
      >
        {state.deliveryDeleteFailed && (
          <Alert
            style={{ marginTop: "1rem" }}
            onClose={() => setState({ ...state, deliveryFailed: false })}
            severity="error"
          >
            {t("common.deliveryManagement.failedToDeleteDelivery")}
          </Alert>
        )}
      </ConfirmDialog>

      <Dialog
        fullWidth={true}
        maxWidth={"sm"}
        open={state.showUpdateDelivery}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle>
          {t("common.deliveryManagement.updateDelivery")}
          <IconButton
            aria-label={t("common.actions.close")}
            className={classes.closeButton}
            onClick={() => {
              setState({ ...state, showUpdateDelivery: false });
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          <TextField
            className={classes.field}
            type="number"
            label={t("common.formFields.deliveredToShelf")}
            value={state.quantity}
            helperText={`${t("common.formFields.currentlyOnShelf")}: ${
              inventoryItem.onShelf
            }`}
            error={calculateQuantity(state.quantity) < 0}
            onChange={e => {
              const value = parseInt(e.target.value);
              setState({
                ...state,
                quantity: value
              });
            }}
          />
          <h3>{`${t(
            "common.deliveryManagement.totalOnShelfAfterDelivery"
          )} ${calculateQuantity(state.quantity)}`}</h3>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: 20
            }}
          >
            <Button
              style={{ width: 200 }}
              variant="contained"
              color="primary"
              disabled={calculateQuantity(state.quantity) < 0}
              onClick={async () => {
                const result = await ProductApi.reportDelivery({
                  id: state.deliveryId,
                  source: { type: "distributor" },
                  destination: {
                    type: "store",
                    id: storeId
                  },
                  item: {
                    productId: inventoryItem.product.id,
                    onShelfQuantityBeforeDelivery: inventoryItem.onShelf,
                    onShelfQuantityDelivered: state.quantity
                  }
                });
                if (result.type === "OK") {
                  const deliveries = state.deliveries.map((d: any) =>
                    d.id === state.deliveryId
                      ? {
                          ...d,
                          onShelfQuantity: state.quantity
                        }
                      : d
                  );

                  onSave(deliveries, calculateQuantity(state.quantity));

                  setState({
                    ...state,
                    deliveries: deliveries,
                    showUpdateDelivery: false,
                    deliverySuccess: true,
                    deliveryFailed: false
                  });
                } else {
                  setState({
                    ...state,
                    deliveryFailed: true,
                    deliverySuccess: false
                  });
                }
              }}
            >
              {t("common.actions.save")}
            </Button>
          </div>
          {state.deliveryFailed && (
            <Alert
              style={{ marginTop: "1rem" }}
              onClose={() => setState({ ...state, deliveryFailed: false })}
              severity="error"
            >
              {t("common.deliveryManagement.failedToUpdateDelivery")}
            </Alert>
          )}
        </DialogContent>
      </Dialog>
      <Snackbar
        open={state.deliverySuccess || state.deliveryDeleteSuccess}
        autoHideDuration={2000}
        onClose={() => {
          setState({
            ...state,
            deliverySuccess: false,
            deliveryDeleteSuccess: false
          });
        }}
      >
        <Alert
          onClose={() =>
            setState({
              ...state,
              deliverySuccess: false,
              deliveryDeleteSuccess: false
            })
          }
          severity="success"
        >
          {state.deliverySuccess
            ? t("common.deliveryManagement.deliveryUpdatedSuccessfully")
            : t("common.deliveryManagement.deliveryDeletedSuccessfully")}
        </Alert>
      </Snackbar>
    </>
  ) : (
    <></>
  );
};
